"use client";
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import DualRangeSlider from "@/components/Common/DualRangeSlider";
import DurationFilter from "@/components/Common/DurationFilter";
import FilterIcon from "../SVG/FilterIcon";
import Button from "../ui/Button";
import {
  CATALOG_MODES,
  DURATION_OPTIONS,
  FINANCING_OPTIONS,
  type CatalogFiltersController,
  type CatalogMode,
} from "@/hooks/useCatalogFilters";

export type Mode = CatalogMode;

export type FilterDrawerProps = {
  open: boolean;
  onClose: () => void;

  /** Filter state + handlers from `useCatalogFilters` — drives the Durée section. */
  controller: CatalogFiltersController;

  // Header/context
  categoryLabel: string;
  showCategoryContext?: boolean;

  // Price controls
  priceMin: number;
  priceMax: number;
  priceRange: [number, number];
  defaultPriceRange: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  onClearPrice: () => void;

  // Mode controls
  selectedMode: Mode | null;
  onModeChange: (mode: Mode) => void;

  // Financing controls
  selectedFinancing: string[];
  onToggleFinancing: (label: string) => void;
  onRemoveFinancing: (label: string) => void;

  // Reset all
  onReset: () => void;

  // Apply
  onApply: () => void;
};

export function FilterDrawer({
  open,
  onClose,
  controller,
  categoryLabel,
  showCategoryContext = true,
  priceMin,
  priceMax,
  priceRange,
  defaultPriceRange,
  onPriceRangeChange,
  onClearPrice,
  selectedMode,
  onModeChange,
  selectedFinancing,
  onToggleFinancing,
  onRemoveFinancing,
  onReset,
  onApply,
}: FilterDrawerProps) {
  const { selectedDuration, handleClearDuration } = controller;

  /** Label of the active Durée option, e.g. "2 jours" — shown as a removable chip. */
  const appliedDuration = useMemo(
    () => DURATION_OPTIONS.find((o) => o.value === selectedDuration)?.label ?? null,
    [selectedDuration],
  );

  // Local toggles for collapsible sections in mobile drawer
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isFinanceOpen, setIsFinanceOpen] = useState(true);

  // Body scroll lock and ESC close
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const priceChanged = useMemo(
    () => priceRange[0] !== defaultPriceRange[0] || priceRange[1] !== defaultPriceRange[1],
    [priceRange, defaultPriceRange]
  );

  const appliedChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove?: () => void }> = [];
    if (priceChanged) {
      chips.push({ key: "price", label: "PRIX", onRemove: onClearPrice });
    }
    selectedFinancing.forEach((f) => {
      chips.push({ key: `fin:${f}`, label: f.toUpperCase(), onRemove: () => onRemoveFinancing(f) });
    });
    if (appliedDuration) {
      chips.push({ key: "duration", label: appliedDuration, onRemove: handleClearDuration });
    }
    return chips;
  }, [priceChanged, onClearPrice, selectedFinancing, onRemoveFinancing, appliedDuration, handleClearDuration]);

  const filterCount = useMemo(() => {
    let count = 0;
    if (priceChanged) count++;
    count += selectedFinancing.length;
    if (appliedDuration) count++;
    if (selectedMode) count++;
    return count;
  }, [priceChanged, selectedFinancing.length, appliedDuration, selectedMode]);

  const modeOptions = CATALOG_MODES;
  const financingOptions = FINANCING_OPTIONS;

  return (
    <div className={`fixed inset-0 z-102 md:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}>
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      {/* Panel (full width) */}
      <div
        className={`absolute inset-y-0 right-0 left-0 h-full w-full bg-white shadow-xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-filters-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 py-4">
          <div className="flex items-center gap-2.5">
            <FilterIcon
              className="w-6 h-6"
              circleColor="#40BFDE"
              strokeColor="#ffffff"
            />
            <p id="mobile-filters-title" className="text-[#1B365E] text-[25px] leading-10 font-light font-red-hat">Filtrez</p>
          </div>
          <div className="flex items-center gap-6">
            <button aria-label="reset" onClick={onReset}>
              <Image src="/assets/icons/reload.svg" alt="reset" width={24} height={24} />
            </button>
            <button aria-label="close" onClick={onClose}>
              <Image src="/assets/icons/icon-close.svg" alt="close" width={24} height={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pt-2 pb-4 flex flex-col gap-6 text-[#1D1D1D] overflow-y-auto h-[calc(100%-120px)]">
          {/* Category context */}
          {showCategoryContext && (
            <div className="flex items-center gap-x-6 gap-y-4 flex-wrap">
              <p className="text-[#1D1D1D] font-bold text-[15px] leading-4.5">Dans la catégorie :</p>
              <div className="inline-flex items-center gap-3 bg-[#1B365E] text-white rounded-full px-4 py-2 text-[8px] leading-2.5 font-bold">
                <span className="inline-flex items-center justify-center text-[8px] scale-[160%] font-normal">×</span>
                <span>{categoryLabel}</span>
              </div>
            </div>
          )}

          {/* Mode buttons */}
          <div className="flex items-center flex-wrap gap-4">
            {modeOptions.map((label) => {
              const active = selectedMode === label;
              return (
                <button
                  key={label}
                  onClick={() => onModeChange(label)}
                  className={`cursor-pointer w-fit text-center px-6 py-[15px] rounded-full border text-xs font-bold leading-3.5 transition-colors ${
                    active ? "bg-[#1B365E] text-white border-[#1B365E]" : "bg-white text-[#1B365E] border-[#EFEFEF] hover:bg-[#F5FBFD]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Applied chips */}
          {appliedChips.length > 0 && (
            <div className="flex flex-wrap gap-6 pt-6 border-t border-[#EFEFEF]">
              {appliedChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-3 ps-1 pe-2 py-[3px] rounded-full border border-[#EFEFEF] bg-white text-[8px] leading-5"
                >
                  <button
                    type="button"
                    aria-label="remove-chip"
                    onClick={chip.onRemove}
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[#1B365E] scale-[160%] -mt-px"
                  >
                    ×
                  </button>
                  <span className="text-[#40BFDE]">{chip.label}</span>
                </span>
              ))}
            </div>
          )}

          {/* Prix */}
          <div className="pt-4 border-t border-[#EFEFEF]">
            <div className="flex items-center justify-between gap-4 mb-3">
              <p className="text-[15px] font-bold leading-4.5 hanken-grotesk tracking-[0.2px]">Prix</p>
              <button
                type="button"
                aria-label={isPriceOpen ? "reduce" : "expand"}
                onClick={() => setIsPriceOpen((v) => !v)}
                className="cursor-pointer w-4 h-4"
              >
                <Image
                  className="w-4 shrink-0"
                  src={isPriceOpen ? "/assets/icons/minus.svg" : "/assets/icons/plus.svg"}
                  alt={isPriceOpen ? "minus" : "plus"}
                  width={16}
                  height={16}
                />
              </button>
            </div>

            {isPriceOpen && (
              <div className="px-1">
                <DualRangeSlider
                  min={priceMin}
                  max={priceMax}
                  step={10}
                  value={priceRange}
                  onChange={onPriceRangeChange}
                />
                <p className="text-sm leading-5 mt-3">Prix : de {priceRange[0]}€ à {priceRange[1]}€</p>
              </div>
            )}
          </div>

          {/* Financement */}
          <div className="pt-4 border-t border-[#EFEFEF]">
            <div className="flex items-center justify-between gap-4 mb-6">
              <p className="text-[15px] font-bold leading-4.5 hanken-grotesk tracking-[0.2px]">Financement</p>
              <button
                type="button"
                aria-label={isFinanceOpen ? "reduce" : "expand"}
                onClick={() => setIsFinanceOpen((v) => !v)}
                className="cursor-pointer w-4 h-4"
              >
                <Image
                  className="w-4 shrink-0"
                  src={isFinanceOpen ? "/assets/icons/minus.svg" : "/assets/icons/plus.svg"}
                  alt={isFinanceOpen ? "minus" : "plus"}
                  width={16}
                  height={16}
                />
              </button>
            </div>
            {isFinanceOpen && (
              <div className="flex flex-col gap-6 text-sm">
                {financingOptions.map((f) => {
                  const checked = selectedFinancing.includes(f);
                  return (
                    <label key={f} className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="cursor-pointer w-6 h-6 rounded border border-[#ABABAB] accent-[#1B365E]"
                        checked={checked}
                        onChange={() => onToggleFinancing(f)}
                      />
                      <span className="text-sm leading-5">{f}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Durée */}
          <DurationFilter
            controller={controller}
            className="py-4 border-y border-t-[#EFEFEF] border-b-[#D5D5D5]"
            groupName="duration-drawer"
          />

          <Button
            variant="primary"
            className='-mt-5.75 w-full text-[15px] font-bold h-9! py-1 px-4 border border-[#EFEFEF]'
            onClick={() => {
              onApply();
              onClose();
            }}
          >
            {`Appliquer les filtres (${filterCount})`}
          </Button>
        </div>
      </div>
    </div>
  );
}
